import React, { useMemo } from 'react';
import { Box, Button, Typography, Link, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { Modal } from '../../../components/modals/Modal';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { useThemes } from '../../../components/Theme/theme';
import { PrivacyPolicyModal } from './PrivacyPolicyModal';
import { useGetUserConsentsQuery, useUpdateUserConsentMutation } from '../../../redux/apis/consents.api';
import { useLocation, useNavigate } from 'react-router-dom';
import { findAiConsent } from '../utils/findAiConsent';
import { useModal } from '../../../hooks/useModal';

const Sparkle = ({ delay, className }: { delay: number; className: string }) => (
  <div className={`sparkle ${className}`} style={{ animationDelay: `${delay}s` }} />
);

const AiConsentIcon = () => (
  <Box
    sx={{
      position: 'relative',
      width: 44,
      height: 44,
      minWidth: 44,
      borderRadius: '10px',
      background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 45%, #0891B2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ position: 'relative', width: 24, height: 24 }}>
      <Sparkle delay={0} className="sparkle-large absolute top-0 left-0" />
      <Sparkle delay={0.5} className="sparkle-medium absolute bottom-0 right-0" />
      <Sparkle delay={1} className="sparkle-small absolute bottom-1 left-2" />
    </Box>
  </Box>
);

export interface ConsentConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onAccepted: () => void;
}

export function ConsentConfirmationModal({ open, onClose, onAccepted }: ConsentConfirmationModalProps) {
  const { theme } = useThemes();
  const navigate = useNavigate();
  const location = useLocation();
  const { isModalOpen: isprivacyPolicyModalOpen, openModal: openPrivacyPolicyModal, closeModal: closePrivacyPolicyModal } = useModal();
  const { data: userConsentsData } = useGetUserConsentsQuery();
  const aiConsent = useMemo(() => findAiConsent(userConsentsData?.consents), [userConsentsData?.consents]);

  const [updateUserConsent, { isLoading: isUpdatingUserConsent }] = useUpdateUserConsentMutation();

  // Clears the #aiConsent hash from the URL and closes the consent modal (Cancel / close icon).
  const handleCancel = () => {
     navigate(
      {
        pathname: location.pathname,
        search: location.search,
      },
      { replace: true }
    );
    onClose();
  };


  const handleOpen = () => {
    // Adds #aiConsent to the URL and opens the Privacy Policy modal.
// The hash is used to automatically scroll to the AI Consent section within the modal.
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: '#aiConsent',
      },
      { replace: true }
    );
    openPrivacyPolicyModal();
  };

  const handleAgreeClick = async () => {
    if (!aiConsent?.consentKey || aiConsent.version == null) {
      showErrorSnackbar('Unable to update consent. Please try again.');
      return;
    }

    try {
      const result = await updateUserConsent({
        consentKey: aiConsent.consentKey,
        version: aiConsent.version,
        status: 'ACCEPTED',
      }).unwrap();

      if (result?.status === 'ACCEPTED') {
        showSuccessSnackbar('AI data processing consent enabled successfully.');
        onAccepted();     
      } else {
        showErrorSnackbar('Unable to update consent. Please try again.');
      }
    } catch (err) {
      console.error('Failed to update user consent:', err);
      showErrorSnackbar('Unable to update consent. Please try again.');
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={isUpdatingUserConsent ? () => {} : handleCancel}
        sx={{
          '& > .MuiBox-root': {
            width: '100%',
            maxWidth: 420,
            borderRadius: '16px',
            overflow: 'hidden',
          },
        }}
      >
        <Modal.Header>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              px: 2.5,
              pt: 2.5,
              pb: 1,
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1 }}>
              <AiConsentIcon />
              <Typography
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '18px', sm: '20px' },
                  lineHeight: 1.3,
                  color: '#1A1A1A',
                  pt: 0.25,
                }}
              >
                AI Data Processing Consent
              </Typography>
            </Box>
            <IconButton
              aria-label="Close consent modal"
              onClick={handleCancel}
              disabled={isUpdatingUserConsent}
              size="small"
              sx={{ color: '#6B7280', mt: 0.25 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Modal.Header>

        <Modal.Body>
          <Box sx={{ px: 2.5, pb: 2, bgcolor: '#fff' }}>
            <Typography
              sx={{
                fontSize: '14px',
                lineHeight: 1.65,
                color: '#1A1A1A',
                mb: 2,
              }}
            >
              LedgerX provides AI-powered features to assist with accounting and business operations. When you use these features,
              information relevant to your request, such as transaction data, invoice details, ledger information, uploaded documents,
              business records, and other data selected by you, may be processed to generate AI-powered insights and other requested
              results.
            </Typography>

            <Box
              sx={{
                bgcolor: '#F3F4F6',
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                borderRadius: '4px',
                px: 1.75,
                py: 1.5,
                mb: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.65,
                  color: '#1A1A1A',
                  fontStyle: 'italic',
                }}
              >
                This information is processed solely for providing AI-powered functionality. By selecting &apos;Agree &amp; Continue&apos;,
                you consent to the processing of such information for the purpose of providing AI-powered features.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', mt: 0.25 }} />
              <Typography sx={{ fontSize: '14px', lineHeight: 1.6, color: '#1A1A1A' }}>
                For more information, please refer to our{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={() => handleOpen()}
                  sx={{ verticalAlign: 'baseline', fontSize: 'inherit', color: 'primary.main' }}
                >
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              bgcolor: '#F9FAFB',
              px: 2.5,
              py: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
            }}
          >
            <LoadingButton
              fullWidth
              variant="contained"
              loading={isUpdatingUserConsent}
              onClick={handleAgreeClick}
              sx={{
                bgcolor: 'primary.main',
                borderRadius: '8px',
                py: 1.1,
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: 'none',
                },
              }}
            >
              Agree & Continue
            </LoadingButton>

            <Button
              fullWidth
              variant="outlined"
              disabled={isUpdatingUserConsent}
              onClick={handleCancel}
              sx={{
                borderRadius: '8px',
                py: 1.1,
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                color: '#1A1A1A',
                borderColor: '#D1D5DB',
                bgcolor: '#fff',
                '&:hover': {
                  bgcolor: '#fff',
                  borderColor: '#9CA3AF',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Modal.Body>
      </Modal>
      {isprivacyPolicyModalOpen ? <PrivacyPolicyModal open={isprivacyPolicyModalOpen} onClose={closePrivacyPolicyModal} /> : null}
    </>
  );
}

export default ConsentConfirmationModal;
